/**
 * Login Component — Direction C reskin
 *
 * Centered card on cream background with brand lockup.
 * Redirects to WorkOS AuthKit for authentication.
 */
import React from 'react';
import { useAuth } from '@shared/context';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import Loader from '../loader/loader.component';
import { getDefaultRouteForRoles } from '../../shared/role-default-route';
import { stashPostLoginRedirect } from '../../shared/post-login-redirect';

import './login.scss';

const Login = () => {
  const { signIn, isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Two ways to arrive at /login with a target:
  //   1. RoleRoute bounced an unauth user here, passing the original
  //      pathname via location.state.from (in-app deep-link).
  //   2. An email link points directly to /login?redirectTo=/transactions/abc.
  // Either source becomes the post-login destination.
  const fromState = (location.state as { from?: string })?.from;
  const fromQuery = searchParams.get('redirectTo');
  const from = fromState || fromQuery;

  if (isAuthenticated) {
    if (from && from !== '/login') {
      return <Navigate to={from} replace />;
    }
    // Use the priority cascade — a staff user with `['CUSTOMER', 'COMPLIANCE']`
    // must land on their staff dashboard, not /accounts/me (bug 1AC21B).
    const defaultRoute = getDefaultRouteForRoles(user?.roles || []);
    return <Navigate to={defaultRoute} replace />;
  }

  const handleSignIn = () => {
    // WorkOS round-trip will lose React Router's location.state. Stash
    // the target in sessionStorage before kicking off the redirect; the
    // /auth/callback page reads it back after auth completes (P1-5 fix).
    if (from && from !== '/login') {
      stashPostLoginRedirect(from);
    }
    signIn();
  };

  return (
    <section className="login-page">
      <div className="login-page__inner">
        <a href="https://esignatureguarantee.com" className="login-page__logo">
          <img src="/assets/images/logos/esig-icon.svg" alt="" width="32" height="32" />
          <span className="login-page__wordmark">eSignature Guarantee</span>
        </a>

        <div className="login-page__card">
          <h1 className="login-page__title">Welcome back</h1>
          <p className="login-page__subtitle">Sign in with your email or Google account</p>

          <div className="login-page__action">
            {isLoading ? (
              <Loader />
            ) : (
              <button
                type="button"
                className="login-page__btn"
                onClick={handleSignIn}
              >
                Sign In
              </button>
            )}
          </div>

          <a href="/forgot-password" className="login-page__forgot">
            Forgot password?
          </a>
        </div>

        <p className="login-page__register">
          Don't have an account? <a href="/join">Create one</a>
        </p>

        <div className="login-page__footer">
          <a href="https://esignatureguarantee.com/privacy">Privacy</a>
          <span>·</span>
          <a href="https://esignatureguarantee.com/terms">Terms</a>
          <span>·</span>
          <a href="https://esignatureguarantee.com/contact">Contact</a>
        </div>
      </div>
    </section>
  );
};

export default Login;
